// src/pages/organization/team/AllTeam.js

// {api_url}/team/get-all-my-organization-teams?page=1&limit=10

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMainPageTitle } from "../../../contexts/PageContext";
import { useLanguage } from "../../../contexts/languageContext";
import { getGenderDisplay } from "../../../components/organization/team/GetGenderDisplay";
import ProfileImage from "../../../components/profileImage";

const AllTeam = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updatePageTitle } = useMainPageTitle();
  const { 
    getDirectionClass, 
    getTextAlign, 
    getSpacingClass, 
    getFormInputClass,
    getSpaceXClass,
    getButtonIconClass,
    getPositionClass
  } = useLanguage();

  useEffect(() => {
    updatePageTitle(t("teams.allTeams"), t("teams.getAllTeamsMessage"));
  }, [updatePageTitle, t]);

  const [page, setPage] = useState(1);
  const [limit] = useState(8);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("createdAt");
  const [order, setOrder] = useState("desc");
  const [selectedGender, setSelectedGender] = useState("");
  const [teamsData, setTeamsData] = useState({
    teams: [],
    pagination: { total: 0, page: 1, pages: 1, limit: 8 },
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const sortOptions = [
    { value: "createdAt", label: t("teams.createdDate") },
    { value: "name", label: t("teams.teamName") },
  ];

  const genderOptions = ["male", "female", "mixed"];

  useEffect(() => {
    const fetchAllTeams = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1/team/get-all-my-organization-teams?page=${page}&limit=${limit}&search=${search}&sort=${sort}&order=${order}${selectedGender ? `&gender=${selectedGender}` : ""}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (!response.ok) throw new Error(t("errors.failedToLoad"));
        const data = await response.json();

        if (data.message === "Teams fetched successfully") {
          setTeamsData({
            teams: data.teams || [],
            pagination: data.pagination || { total: 0, page: 1, pages: 1, limit },
          });
        } else {
          throw new Error(data.message || t("errors.failedToLoad"));
        }
      } catch (err) {
        setError(err.message);
        setTeamsData({
          teams: [],
          pagination: { total: 0, page: 1, pages: 1, limit },
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllTeams();
  }, [page, limit, search, sort, order, selectedGender, t]);

  const TeamCard = ({ team }) => {
    // Calculate number of players - ensure members is an array
    const playerCount = Array.isArray(team.members) 
      ? team.members.filter((member) => member.type === "player").length 
      : 0;

    const handleTeamClick = (teamId) => {
      navigate(`/organization/team/${teamId}`);
    };

    return (
      <div
        className={`group bg-gradient-to-br from-white via-gray-50 to-orange-50 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 cursor-pointer border border-gray-100 overflow-hidden ${getDirectionClass()}`}
        onClick={() => handleTeamClick(team._id)}
      >
        {/* Decorative Top Border */}
        <div className="h-1 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500"></div>
        
        <div className="p-6">
          {/* Logo Section with Enhanced Effects */}
          <div className="relative mb-6 aspect-square rounded-xl overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 group-hover:shadow-xl transition-shadow duration-300">
            {/* Background Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-orange-400/10 to-red-500/10 group-hover:from-orange-400/20 group-hover:to-red-500/20 transition-all duration-300"></div>
            
            <ProfileImage
              image={team.logo}
              alt={team.name}
              user={team}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            
                         {/* Status Indicator - only show if no pending requests */}

             {/* Pending Requests Badge - only show when there are pending requests */}
             {team.pendingRequestsCount > 0 && (
               <div className="absolute top-3 left-3">
                 <div className="flex items-center space-x-1 bg-red-500 text-white px-2 py-1 rounded-full shadow-lg">
                   <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                     <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                   </svg>
                   <span className="text-xs font-bold">{team.pendingRequestsCount}</span>
                 </div>
               </div>
             )}

            {/* Members Count Overlay */}
            <div className="absolute bottom-3 right-3">
              <div className="flex items-center space-x-1 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full shadow-md">
                <svg className="w-3 h-3 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                </svg>
                <span className="text-xs font-medium text-gray-700">{playerCount}</span>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="space-y-3">
            {/* Team Name */}
            <div className="flex items-center justify-between">
              <h3 className={`font-bold text-xl text-gray-800 truncate group-hover:text-orange-600 transition-colors duration-300 ${getTextAlign()}`}>
                {team.name}
              </h3>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                <div className="w-1 h-1 bg-orange-300 rounded-full"></div>
              </div>
            </div>

            {/* Team Handle */}
            <div className="flex items-center space-x-2">
              <div className="p-1 bg-gradient-to-r from-orange-100 to-red-100 rounded-md">
                <svg className="w-3 h-3 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
              <p className={`text-sm font-medium text-gray-600 truncate ${getTextAlign()}`}>@{team.uniqueName}</p>
            </div>

            {/* Enhanced Badges */}
            <div className={`flex flex-wrap gap-2 ${getSpaceXClass(2)}`}>
              <span className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-full text-xs font-medium shadow-md hover:shadow-lg transition-shadow duration-300">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                </svg>
                {team.sport}
              </span>
              <span className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-full text-xs font-medium shadow-md hover:shadow-lg transition-shadow duration-300">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
                {team.type}
              </span>
            </div>

            {/* Team Stats Bar */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-3 border border-gray-200">
              <div className={`flex items-center justify-between text-sm ${getSpaceXClass(2)}`}>
                <div className="flex items-center space-x-2 text-gray-600">
                  <svg className="w-4 h-4 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium capitalize">{getGenderDisplay(team.gender)}</span>
                </div>
                <div className="flex items-center space-x-2">
                                  <div className="text-right">
                  <div className="text-lg font-bold text-orange-600">{playerCount}</div>
                  <div className="text-xs text-gray-500">{t('labels.players')}</div>
                </div>
                </div>
              </div>
            </div>
          </div>

          {/* Hover Effect Indicator */}
          <div className="mt-4 pt-3 border-t border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="flex items-center justify-center space-x-2 text-orange-600">
              <span className="text-sm font-medium">View Details</span>
              <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen p-8 ${getDirectionClass()}`}>
      {/* Header Section */}
      <div className={`flex flex-col md:flex-row md:items-center justify-between mb-8 space-y-4 md:space-y-0`}>
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/organization/team/create')}
            className={`${getSpacingClass('m', 'l', 4)} px-4 py-2 bg-[#F15213] text-white rounded-lg hover:bg-[#d14711] transition-colors flex items-center gap-2`}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className={`h-5 w-5 ${getButtonIconClass('left')}`}
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path 
                fillRule="evenodd" 
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" 
                clipRule="evenodd" 
              />
            </svg>
            {t("teams.createNewTeam")}
          </button>
        </div>

        <div className={`flex flex-col md:flex-row items-stretch md:items-center gap-4`}>
          {/* Search Bar */}
          <div className="relative">
            <input
              type="text"
              placeholder={t("teams.searchTeams")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`w-full md:w-64 ${getSpacingClass('p', 'l', 10)} ${getSpacingClass('p', 'r', 4)} py-2 rounded-lg border-0 ring-1 ring-gray-200 focus:ring-2 focus:ring-blue-500 outline-none ${getFormInputClass()}`}
            />
            <svg
              className={`w-5 h-5 absolute ${getPositionClass('left', 3)} top-2.5 text-gray-400`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>

          <div className={`flex items-center gap-2`}>
            {/* Sort Dropdown */}
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className={`px-4 py-2 rounded-lg border-0 ring-1 ring-gray-200 focus:ring-2 focus:ring-blue-500 outline-none bg-white ${getFormInputClass()}`}
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t("teams.sortBy")} {option.label}
                </option>
              ))}
            </select>

            {/* Order Toggle Button */}
            <button
              onClick={() => setOrder(order === "asc" ? "desc" : "asc")}
              className={`px-4 py-2 rounded-lg bg-white ring-1 ring-gray-200 hover:bg-gray-50 transition-colors flex items-center gap-2`}
              title={order === "asc" ? t("common.ascending") : t("common.descending")}
            >
              {order === "asc" ? (
                <>
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4h13M3 8h9M3 12h5m0 0v8m0-8h2"
                    />
                  </svg>
                  <span className="hidden md:inline">{t("common.aToZ")}</span>
                </>
              ) : (
                <>
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4h13M3 8h9M3 12h5m0 0v8m0-8h2"
                    />
                  </svg>
                  <span className="hidden md:inline">{t("common.zToA")}</span>
                </>
              )}
            </button>
          </div>

          {/* Gender Filter */}
          <select
            value={selectedGender}
            onChange={(e) => setSelectedGender(e.target.value)}
            className={`px-4 py-2 rounded-lg border-0 ring-1 ring-gray-200 focus:ring-2 focus:ring-blue-500 outline-none bg-white ${getFormInputClass()}`}
          >
            <option value="">{t("teams.allGenders")}</option>
            {genderOptions.map((gender) => (
              <option key={gender} value={gender}>
                {t(`teams.${gender}`)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F15213]"></div>
          <span className={`${getSpacingClass('m', 'l', 3)} text-gray-600`}>{t("common.loading")}</span>
        </div>
      )}

      {/* Error State */}
      {!isLoading && error && (
        <div className="flex flex-col justify-center items-center h-64 text-red-500">
          <div className={`text-center ${getTextAlign()}`}>
            <p className="text-lg font-medium mb-2">{t("errors.somethingWentWrong")}</p>
            <p className="text-sm">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              {t("common.tryAgain")}
            </button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && teamsData.teams.length === 0 && (
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <svg
            className="w-16 h-16 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            />
          </svg>
          <p className={`text-xl font-medium ${getTextAlign()}`}>{t("teams.noTeamsFound")}</p>
          <p className={`text-sm mt-2 ${getTextAlign()}`}>{t("teams.adjustFiltersMessage")}</p>
          <button
            onClick={() => navigate('/organization/team/create')}
            className="mt-4 px-6 py-2 bg-[#F15213] text-white rounded-lg hover:bg-[#d14711] transition-colors"
          >
            {t("teams.createFirstTeam")}
          </button>
        </div>
      )}

      {/* Teams Grid */}
      {!isLoading && !error && teamsData.teams.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {teamsData.teams.map((team) => (
              <TeamCard key={team._id} team={team} />
            ))}
          </div>

          {/* Pagination */}
          {teamsData.pagination.pages > 1 && (
            <div className={`flex justify-center mt-8 ${getSpaceXClass(2)}`}>
              {[...Array(teamsData.pagination.pages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i + 1)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    page === i + 1
                      ? "bg-[#F15213] text-white"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AllTeam;
